// 규칙 간파 — 숨은 규칙에 맞는 것/아닌 것 예시를 보고 새 카드를 분류한 뒤, 규칙이 무엇이었는지 고른다.
// 처음 보는 게임 규칙을 빨리 파악하는 힘(귀납 추론)을 훈련한다. 레벨이 오르면 규칙이 복합 조건이 되고 예시가 줄어든다.
import { hasBatchim, makeRng, pick, shuffle, type Rng } from './rng';

export type Color = 'red' | 'blue' | 'green' | 'yellow';
export type Shape = 'circle' | 'triangle' | 'square' | 'diamond';
export type Fill = 'solid' | 'hollow';
export interface Token { color: Color; shape: Shape; fill: Fill; count: number }

type Pred =
  | { t: 'color'; v: Color }
  | { t: 'shape'; v: Shape }
  | { t: 'fill'; v: Fill }
  | { t: 'count'; op: 'ge' | 'le' | 'eq'; n: number }
  | { t: 'parity'; even: boolean };
type Kind = 'p' | 'not' | 'and' | 'or' | 'andnot' | 'xor' | 'if';
export type Rule = { k: 'p'; a: Pred } | { k: 'not'; a: Pred } | { k: Exclude<Kind, 'p' | 'not'>; a: Pred; b: Pred };

export interface RulePuzzle {
  examples: { token: Token; ok: boolean }[];
  tests: { token: Token; ok: boolean }[];
  answer: string;
  choices: string[];
  /** 검사용 — 답 규칙과 오답 규칙 (화면에는 문장만 보인다) */
  rule: Rule;
  wrongRules: Rule[];
}

const COLORS: Color[] = ['red', 'blue', 'green', 'yellow'];
const SHAPES: Shape[] = ['circle', 'triangle', 'square', 'diamond'];
export const COLOR_NAME: Record<Color, string> = { red: '빨강', blue: '파랑', green: '초록', yellow: '노랑' };
export const SHAPE_NAME: Record<Shape, string> = { circle: '동그라미', triangle: '세모', square: '네모', diamond: '마름모' };
const FILL_NAME: Record<Fill, string> = { solid: '꽉 찬 모양', hollow: '빈 모양' };

interface Level { colors: number; shapes: number; fills: boolean; maxCount: number; preds: Pred['t'][]; kinds: Kind[]; examples: number; tests: number }
const ALL_PREDS: Pred['t'][] = ['color', 'shape', 'fill', 'count', 'parity'];
const LEVELS: Level[] = [
  { colors: 3, shapes: 3, fills: false, maxCount: 3, preds: ['color', 'shape'], kinds: ['p'], examples: 4, tests: 10 },
  { colors: 3, shapes: 3, fills: true, maxCount: 3, preds: ['color', 'shape', 'fill', 'count'], kinds: ['p'], examples: 4, tests: 10 },
  { colors: 3, shapes: 3, fills: true, maxCount: 4, preds: ALL_PREDS, kinds: ['not', 'p'], examples: 4, tests: 10 },
  { colors: 3, shapes: 3, fills: true, maxCount: 3, preds: ALL_PREDS, kinds: ['and', 'p'], examples: 4, tests: 10 },
  { colors: 3, shapes: 3, fills: true, maxCount: 3, preds: ALL_PREDS, kinds: ['or', 'and', 'not'], examples: 4, tests: 10 },
  { colors: 3, shapes: 3, fills: true, maxCount: 4, preds: ALL_PREDS, kinds: ['andnot', 'or', 'and'], examples: 3, tests: 10 },
  { colors: 4, shapes: 4, fills: true, maxCount: 4, preds: ALL_PREDS, kinds: ['xor', 'andnot', 'or', 'and'], examples: 3, tests: 10 },
  { colors: 4, shapes: 4, fills: true, maxCount: 4, preds: ALL_PREDS, kinds: ['if', 'xor', 'andnot', 'or'], examples: 3, tests: 10 },
  { colors: 4, shapes: 4, fills: true, maxCount: 4, preds: ALL_PREDS, kinds: ['if', 'xor', 'andnot', 'or', 'and'], examples: 3, tests: 12 },
  { colors: 4, shapes: 4, fills: true, maxCount: 4, preds: ALL_PREDS, kinds: ['if', 'xor', 'andnot', 'or', 'and'], examples: 2, tests: 12 },
];
/** 분류 1문제 = 1점, 규칙 고르기 = 2점 */
export const RULE_POINTS = 2;

const attr = (p: Pred): string => (p.t === 'parity' ? 'count' : p.t);

function evalPred(p: Pred, t: Token): boolean {
  switch (p.t) {
    case 'color': return t.color === p.v;
    case 'shape': return t.shape === p.v;
    case 'fill': return t.fill === p.v;
    case 'count': return p.op === 'ge' ? t.count >= p.n : p.op === 'le' ? t.count <= p.n : t.count === p.n;
    case 'parity': return (t.count % 2 === 0) === p.even;
  }
}

export function evalRule(r: Rule, t: Token): boolean {
  const a = evalPred(r.a, t);
  if (r.k === 'p') return a;
  if (r.k === 'not') return !a;
  const b = evalPred(r.b, t);
  switch (r.k) {
    case 'and': return a && b;
    case 'or': return a || b;
    case 'andnot': return a && !b;
    case 'xor': return a !== b;
    case 'if': return !a || b;
  }
}

function predText(p: Pred): string {
  switch (p.t) {
    case 'color': return COLOR_NAME[p.v];
    case 'shape': return SHAPE_NAME[p.v];
    case 'fill': return FILL_NAME[p.v];
    case 'count': return p.op === 'ge' ? `${p.n}개 이상` : p.op === 'le' ? `${p.n}개 이하` : `딱 ${p.n}개`;
    case 'parity': return p.even ? '짝수 개' : '홀수 개';
  }
}

export function describe(r: Rule): string {
  const a = predText(r.a);
  if (r.k === 'p') return a;
  if (r.k === 'not') return `${a} 아님`;
  const b = predText(r.b);
  switch (r.k) {
    case 'and': return `${a} 그리고 ${b}`;
    case 'or': return `${a} 또는 ${b}`;
    case 'andnot': return `${a} 그리고 ${b} 아님`;
    case 'xor': return `${a}·${b} 중 딱 하나만`;
    case 'if': return `${a}${hasBatchim(a) ? '이면' : '면'} 반드시 ${b}`;
  }
}

function space(lv: Level): Token[] {
  const out: Token[] = [];
  for (const color of COLORS.slice(0, lv.colors))
    for (const shape of SHAPES.slice(0, lv.shapes))
      for (const fill of (lv.fills ? ['solid', 'hollow'] : ['solid']) as Fill[])
        for (let count = 1; count <= lv.maxCount; count++) out.push({ color, shape, fill, count });
  return out;
}

function randPred(rng: Rng, lv: Level, not?: string): Pred {
  const types = lv.preds.filter((t) => (t === 'parity' ? 'count' : t) !== not && (t !== 'fill' || lv.fills));
  const t = pick(rng, types);
  if (t === 'color') return { t, v: pick(rng, COLORS.slice(0, lv.colors)) };
  if (t === 'shape') return { t, v: pick(rng, SHAPES.slice(0, lv.shapes)) };
  if (t === 'fill') return { t, v: pick(rng, ['solid', 'hollow'] as Fill[]) };
  if (t === 'parity') return { t, even: rng() < 0.5 };
  const op = pick(rng, ['ge', 'le', 'eq'] as const);
  const n = op === 'ge' ? 2 + Math.floor(rng() * (lv.maxCount - 1)) : op === 'le' ? 1 + Math.floor(rng() * (lv.maxCount - 1)) : 1 + Math.floor(rng() * lv.maxCount);
  return { t, op, n };
}

function randRule(rng: Rng, lv: Level, kind: Kind): Rule {
  const a = randPred(rng, lv);
  if (kind === 'p' || kind === 'not') return { k: kind, a };
  return { k: kind, a, b: randPred(rng, lv, attr(a)) };
}

const signature = (r: Rule, all: Token[]): string => all.map((t) => (evalRule(r, t) ? '1' : '0')).join('');

/** 답과 헷갈리기 쉬운 오답: 조건 하나만, 연결만 바꾼 것, 같은 꼴의 다른 규칙 */
function nearMisses(r: Rule): Rule[] {
  if (r.k === 'p' || r.k === 'not') return [{ k: r.k === 'p' ? 'not' : 'p', a: r.a }];
  const { a, b } = r;
  const kinds: Kind[] = ['and', 'or', 'andnot', 'xor', 'if'];
  return [{ k: 'p', a }, { k: 'p', a: b }, ...kinds.filter((k) => k !== r.k).map((k) => ({ k, a, b }) as Rule), { k: r.k, a: b, b: a } as Rule];
}

export function makeRulePuzzle(level: number, seed: number): RulePuzzle {
  const rng = makeRng(seed);
  const lv = LEVELS[Math.min(LEVELS.length, Math.max(1, level)) - 1];
  const all = space(lv);
  // 1) 규칙: 가장 새로 열린 꼴을 자주, 너무 한쪽으로 치우친 규칙(맞는 것이 10% 미만·90% 초과)은 버린다
  let rule: Rule;
  for (;;) {
    const kind = rng() < 0.6 ? lv.kinds[0] : pick(rng, lv.kinds);
    rule = randRule(rng, lv, kind);
    const share = all.filter((t) => evalRule(rule, t)).length / all.length;
    if (share >= 0.1 && share <= 0.9) break;
  }
  const pos = shuffle(rng, all.filter((t) => evalRule(rule, t)));
  const neg = shuffle(rng, all.filter((t) => !evalRule(rule, t)));
  // 2) 예시(맞음 n + 아님 n) 다음 시험 카드 — 겹치지 않게 뽑고, 모자라면 다시 쓴다
  let pi = 0;
  let ni = 0;
  const take = (ok: boolean): Token => (ok ? pos[pi++ % pos.length] : neg[ni++ % neg.length]);
  const examples = [...Array.from({ length: lv.examples }, () => ({ token: take(true), ok: true })),
    ...Array.from({ length: lv.examples }, () => ({ token: take(false), ok: false }))];
  const half = lv.tests / 2;
  const tests = shuffle(rng, [...Array.from({ length: half }, () => true), ...Array.from({ length: lv.tests - half }, () => false)])
    .map((ok) => ({ token: take(ok), ok }));
  // 3) 보기: 답 + 오답 3개. 오답은 답과 '보여 준 카드 중 적어도 하나'에서 판정이 달라야 한다 (구별할 근거가 있어야 공정)
  const shown = [...examples, ...tests].map((e) => e.token);
  const sig = signature(rule, all);
  const seenSig = new Set([sig]);
  const seenText = new Set([describe(rule)]);
  const wrong: Rule[] = [];
  const consider = (r: Rule) => {
    if (wrong.length >= 3) return;
    const s = signature(r, all);
    const text = describe(r);
    if (seenSig.has(s) || seenText.has(text)) return;
    if (!shown.some((t) => evalRule(r, t) !== evalRule(rule, t))) return;
    seenSig.add(s);
    seenText.add(text);
    wrong.push(r);
  };
  for (const r of shuffle(rng, nearMisses(rule)).slice(0, 2)) consider(r);
  for (let i = 0; wrong.length < 3 && i < 500; i++) consider(randRule(rng, lv, pick(rng, lv.kinds)));
  for (let i = 0; wrong.length < 3 && i < 500; i++) consider(randRule(rng, lv, pick(rng, ['p', 'not', 'and', 'or'] as Kind[])));
  return {
    examples: shuffle(rng, examples), tests, answer: describe(rule), choices: shuffle(rng, [rule, ...wrong].map(describe)), rule, wrongRules: wrong,
  };
}
